import canUseDOM from './canUseDOM'
import { SHARED_PATHS } from './paths'

const STYTCH_LOCALHOST_PATTERN = /^(localhost|\[::1\]|127(\.\d{1,3}){3})$/

/**
 * Stytch cookie domain for a hostname. Server cookie writes use this rule too, so they replace
 * the SDK cookie and do not add a second cookie with the same name.
 * Returns the full hostname to scope cookies to individual organization subdomains
 * (e.g., geha.hs-pp-317.preview.poweredbyash.io), so each organization has its own isolated
 * session. Returns `localhost` for loopback hosts, where the SDK sets no cookie Domain.
 * Backend API calls use Authorization headers instead of cookies.
 */
export const getStytchCookieDomainForHostname = (hostname: string): string =>
  STYTCH_LOCALHOST_PATTERN.test(hostname.toLowerCase()) ? 'localhost' : hostname

export const getStytchCookieDomain = (): string => {
  // Return localhost for server-side rendering or when DOM is not available
  if (!canUseDOM) {
    return 'localhost'
  }

  return getStytchCookieDomainForHostname(window.location.hostname)
}

const LOCAL_STYTCH_ORIGIN = 'http://localhost:3000'
const PRODUCTION_STYTCH_REDIRECT_ORIGIN = 'https://poweredbyash.io'
const STAGING_STYTCH_REDIRECT_ORIGIN = 'https://staging.poweredbyash.io'

function isLocalStytchHost(): boolean {
  return !canUseDOM || getStytchCookieDomain() === 'localhost'
}

function isProductionStytchHost(): boolean {
  return /(?<!\.preview|\.staging)\.poweredbyash\.io$/.test(location.hostname)
}

/**
 * Origin Stytch is allowed to redirect back to for magic links, password
 * reset, One Tap, and full-page Google.
 *
 * Production uses a static origin because Stytch does not support wildcard
 * tenant subdomains. Preview and staging keep the current origin so those
 * flows return to the host the user is on.
 */
export const getStytchRedirectOrigin = (): string => {
  if (isLocalStytchHost()) {
    return LOCAL_STYTCH_ORIGIN
  }

  if (isProductionStytchHost()) {
    return PRODUCTION_STYTCH_REDIRECT_ORIGIN
  }

  return location.origin
}

/**
 * Get the dynamic redirect URL for Stytch based on current environment
 * This ensures magic links redirect to the same environment the user is on
 *
 * Production: Returns static URL (https://poweredbyash.io/authenticate-init)
 * Preview/Staging: Returns the current origin
 */
export const getStytchRedirectURL = (): string => {
  return `${getStytchRedirectOrigin()}${SHARED_PATHS.authenticateInit}`
}

/**
 * Origin for the Google OAuth popup callback. Stytch requires a registered
 * static URL, so preview and staging always use the shared staging origin.
 * Independent of getStytchRedirectOrigin() so the popup flag does not
 * retarget magic links or password resets.
 */
export const getGoogleOAuthPopupCallbackOrigin = (): string => {
  if (isLocalStytchHost()) {
    return LOCAL_STYTCH_ORIGIN
  }

  if (isProductionStytchHost()) {
    return PRODUCTION_STYTCH_REDIRECT_ORIGIN
  }

  return STAGING_STYTCH_REDIRECT_ORIGIN
}

export const getGoogleOAuthPopupCallbackURL = (): string => {
  return `${getGoogleOAuthPopupCallbackOrigin()}${SHARED_PATHS.oauthGooglePopupCallback}`
}
